import { Link } from 'react-router-dom';
import { Home, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { useDynamicFavicon } from '@/hooks/useDynamicFavicon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface Town {
  id: string;
  name: string;
  slug: string;
}

interface HeaderProps {
  siteName?: string;
  logoUrl?: string | null;
  faviconUrl?: string | null;
  towns?: Town[];
  showAdmin?: boolean;
  enableMarketValueReport?: boolean;
  enableCommunityGuide?: boolean;
  enableMortgageInfo?: boolean;
}

export function Header({ 
  siteName = 'Real Estate', 
  logoUrl, 
  faviconUrl, 
  towns = [], 
  showAdmin = false,
  enableMarketValueReport = false,
  enableCommunityGuide = false,
  enableMortgageInfo = false,
}: HeaderProps) {
  useDynamicFavicon(faviconUrl);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const hasOffers = enableMarketValueReport || enableCommunityGuide || enableMortgageInfo;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-primary/20 bg-primary text-primary-foreground">
      <div className="container flex h-16 items-center px-4 sm:px-6 lg:px-8 relative">
        {/* Logo - Left - High priority as it's above the fold */}
        {logoUrl && (
          <Link to="/" className="absolute left-4 sm:left-6 lg:left-8">
            <img 
              src={logoUrl} 
              alt={siteName} 
              className="h-14 max-w-[220px] object-contain"
              fetchPriority="high"
              decoding="async"
              width={220}
              height={56}
            />
          </Link>
        )}
        
        {/* Site Name - Centered */}
        <Link to="/" className="flex-1 flex items-center justify-center gap-2 sm:gap-3 font-display text-base sm:text-xl font-semibold text-primary-foreground px-12 sm:px-0">
          {!logoUrl && <Home className="h-5 w-5 sm:h-6 sm:w-6" />}
          <span className="truncate">{siteName}</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="absolute right-4 hidden md:flex items-center gap-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="text-primary-foreground hover:bg-primary-foreground/10">
                <Menu className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-background">
              <DropdownMenuItem asChild>
                <Link to="/buyers" className="cursor-pointer">Buyers</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/sellers" className="cursor-pointer">Sellers</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/blog" className="cursor-pointer">Blog</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/towns" className="cursor-pointer">Locations</Link>
              </DropdownMenuItem>
              {hasOffers && <DropdownMenuSeparator />}
              {enableMarketValueReport && (
                <DropdownMenuItem asChild>
                  <Link to="/market-value-report" className="cursor-pointer">Market Value Report</Link>
                </DropdownMenuItem>
              )}
              {enableCommunityGuide && (
                <DropdownMenuItem asChild>
                  <Link to="/community-guide" className="cursor-pointer">Community Guides</Link>
                </DropdownMenuItem>
              )}
              {enableMortgageInfo && (
                <DropdownMenuItem asChild>
                  <Link to="/mortgages" className="cursor-pointer">Mortgages</Link>
                </DropdownMenuItem>
              )}
              {showAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="cursor-pointer">Admin</Link>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        {/* Mobile Menu Button */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden absolute right-4 text-primary-foreground hover:bg-primary-foreground/10"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-primary/20 bg-primary animate-fade-in">
          <nav className="container py-4 flex flex-col gap-3 px-4">
            <Link
              to="/buyers"
              className="font-body text-sm font-medium text-primary-foreground/80 hover:text-primary-foreground transition-colors py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Buyers
            </Link>
            <Link
              to="/sellers"
              className="font-body text-sm font-medium text-primary-foreground/80 hover:text-primary-foreground transition-colors py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Sellers
            </Link>
            <div className="border-t border-primary-foreground/20 my-2" />
            <Link
              to="/blog"
              className="font-body text-sm font-medium text-primary-foreground/80 hover:text-primary-foreground transition-colors py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Blog
            </Link>
            <Link
              to="/towns"
              className="font-body text-sm font-medium text-primary-foreground/80 hover:text-primary-foreground transition-colors py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Locations
            </Link>
            {hasOffers && <div className="border-t border-primary-foreground/20 my-2" />}
            {enableMarketValueReport && (
              <Link
                to="/market-value-report"
                className="font-body text-sm font-medium text-primary-foreground/80 hover:text-primary-foreground transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Market Value Report
              </Link>
            )}
            {enableCommunityGuide && (
              <Link
                to="/community-guide"
                className="font-body text-sm font-medium text-primary-foreground/80 hover:text-primary-foreground transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Community Guides
              </Link>
            )}
            {enableMortgageInfo && (
            <Link
                to="/mortgages"
                className="font-body text-sm font-medium text-primary-foreground/80 hover:text-primary-foreground transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Mortgages
              </Link>
            )}
            {towns.length > 0 && <div className="border-t border-primary-foreground/20 my-2" />}
            {towns.map((town) => (
              <Link
                key={town.id}
                to={`/${town.slug}`}
                className="font-body text-sm font-medium text-primary-foreground/80 hover:text-primary-foreground transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {town.name}
              </Link>
            ))}
            {showAdmin && (
              <>
                <div className="border-t border-primary-foreground/20 my-2" />
                <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline-dark" size="sm" className="w-full">Admin</Button>
                </Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
